import { useEffect, useRef } from 'react'

/**
 * Alt+Space can't go through `useHotkeys`: on macOS Option+Space emits a
 * non-breaking space as `event.key`, which @tanstack/hotkeys doesn't alias to
 * 'Space', so the hotkey never matches. Match on `event.code` instead, which is
 * unaffected by modifiers and keyboard layout.
 */
export function useStartShowHotkey(callback: () => void) {
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  // useEffect: a window-level keydown listener is a subscription to something
  // outside React, and there is no element here to attach an onKeyDown to.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return
      if (!event.altKey) return
      if (event.ctrlKey || event.metaKey || event.shiftKey) return

      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      callbackRef.current()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])
}
